/**
 * Walk Unlayer design JSON and collect image content blocks.
 * Unlayer 1.157 content nodes always carry a string `id`; blocks without id are skipped
 * so patchImageById never matches on `undefined === undefined`.
 */
export const collectDesignImages = (design) => {
  const images = [];

  const walk = (node) => {
    if (!node || typeof node !== "object") {
      return;
    }

    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }

    if (
      node.type === "image" &&
      typeof node.id === "string" &&
      node.id &&
      node.values?.src &&
      typeof node.values.src === "object"
    ) {
      images.push({
        id: node.id,
        url: node.values.src.url || "",
        width: node.values.src.width,
        height: node.values.src.height,
      });
    }

    Object.values(node).forEach(walk);
  };

  walk(design);
  return images;
};

export const patchImageById = (design, imageId, url, size) => {
  if (typeof imageId !== "string" || !imageId) {
    throw new Error("imageId is required");
  }

  const clone = JSON.parse(JSON.stringify(design));
  let replaced = false;

  const walk = (node) => {
    if (!node || typeof node !== "object" || replaced) {
      return;
    }

    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }

    if (node.type === "image" && node.id === imageId && node.values?.src) {
      const nextSrc = { url };
      if (size?.width && size?.height) {
        nextSrc.width = size.width;
        nextSrc.height = size.height;
      }
      node.values.src = nextSrc;
      replaced = true;
      return;
    }

    Object.values(node).forEach(walk);
  };

  walk(clone);

  if (!replaced) {
    throw new Error("Selected image block was not found in the design");
  }

  return clone;
};

export const saveDesign = (editor) =>
  new Promise((resolve, reject) => {
    if (!editor?.saveDesign) {
      reject(new Error("Editor is not ready"));
      return;
    }
    editor.saveDesign((design) => resolve(design));
  });

/**
 * Patch one image block via loadDesign.
 * Trade-off: Unlayer has no public API to update a single content value,
 * so loadDesign resets undo/redo history and may jump scroll position.
 */
export const applyImageUrlById = async (editor, imageId, url, size) => {
  const design = await saveDesign(editor);
  editor.loadDesign(patchImageById(design, imageId, url, size));
  return url;
};
